import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { AsiaBetPageType, DisplaySection, EventDateType, MarketPage, OddsDaylightType, OddsDisplayMode } from '@/interface/enum';
import { useAsiaSportMarketInfoStore } from '@/store/asiaSportMarketInfoStore';
import { useOddsDisplaySettingsStore } from '@/store/oddsDisplaySettingsStore';

const TODAY_BY_DAYLIGHT: Record<'single' | 'double', Partial<Record<OddsDaylightType, AsiaBetPageType>>> = {
    single: {
        [OddsDaylightType.Within3Hours]: AsiaBetPageType.SingleLineToday3Hours,
        [OddsDaylightType.Within6Hours]: AsiaBetPageType.SingleLineToday6Hours,
    },
    double: {
        [OddsDaylightType.Within3Hours]: AsiaBetPageType.DoubleLineToday3Hours,
        [OddsDaylightType.Within6Hours]: AsiaBetPageType.DoubleLineToday6Hours,
    },
};
const TODAY_DEFAULT: Record<'single' | 'double', AsiaBetPageType> = {
    single: AsiaBetPageType.SingleLineToday,
    double: AsiaBetPageType.DoubleLineToday,
};

const EARLY_MARKET_BY_DATE: Record<'single' | 'double', Partial<Record<EventDateType, AsiaBetPageType>>> = {
    single: {
        [EventDateType.Plus1]: AsiaBetPageType.SingleLineEarlyMarketPlus1,
        [EventDateType.Plus2]: AsiaBetPageType.SingleLineEarlyMarketPlus2,
        [EventDateType.Plus3]: AsiaBetPageType.SingleLineEarlyMarketPlus3,
        [EventDateType.Plus4]: AsiaBetPageType.SingleLineEarlyMarketPlus4,
        [EventDateType.Plus5]: AsiaBetPageType.SingleLineEarlyMarketPlus5,
        [EventDateType.Plus6Over]: AsiaBetPageType.SingleLineEarlyMarketPlus6Over,
    },
    double: {
        [EventDateType.Plus1]: AsiaBetPageType.DoubleLineEarlyMarketPlus1,
        [EventDateType.Plus2]: AsiaBetPageType.DoubleLineEarlyMarketPlus2,
        [EventDateType.Plus3]: AsiaBetPageType.DoubleLineEarlyMarketPlus3,
        [EventDateType.Plus4]: AsiaBetPageType.DoubleLineEarlyMarketPlus4,
        [EventDateType.Plus5]: AsiaBetPageType.DoubleLineEarlyMarketPlus5,
        [EventDateType.Plus6Over]: AsiaBetPageType.DoubleLineEarlyMarketPlus6Over,
    },
};
const EARLY_MARKET_DEFAULT: Record<'single' | 'double', AsiaBetPageType> = {
    single: AsiaBetPageType.SingleLineEarlyMarket,
    double: AsiaBetPageType.DoubleLineEarlyMarket,
};

const MARKET_PAGE_TO_BET_PAGE: Partial<
Record<MarketPage, { single: AsiaBetPageType, double: AsiaBetPageType }>
> = {
    [MarketPage.Live]: { single: AsiaBetPageType.SingleLineLive, double: AsiaBetPageType.DoubleLineLive },
    [MarketPage.MixParlay]: { single: AsiaBetPageType.MixParlay, double: AsiaBetPageType.MixParlay },
    [MarketPage.Outright]: { single: AsiaBetPageType.Outright, double: AsiaBetPageType.Outright },
    [MarketPage.MyFavorites]: {
        single: AsiaBetPageType.SingleLineMyFavorites,
        double: AsiaBetPageType.DoubleLineMyFavorites,
    },
};

function getBetPageFromMarket(
    marketPage: MarketPage,
    daylightType: OddsDaylightType,
    eventDate: EventDateType,
    isSingle: boolean,
): AsiaBetPageType {
    const lineVariant = (['double', 'single'] as const)[Number(isSingle)];

    const directMapping = MARKET_PAGE_TO_BET_PAGE[marketPage];
    if (directMapping) {
        return directMapping[lineVariant];
    }

    if (marketPage === MarketPage.Today) {
        return TODAY_BY_DAYLIGHT[lineVariant][daylightType] ?? TODAY_DEFAULT[lineVariant];
    }

    if (marketPage === MarketPage.EarlyMarket) {
        return EARLY_MARKET_BY_DATE[lineVariant][eventDate] ?? EARLY_MARKET_DEFAULT[lineVariant];
    }

    return AsiaBetPageType.Unknown;
}

const displaySectionMap: Record<DisplaySection, AsiaBetPageType> = {
    [DisplaySection.Unknown]: AsiaBetPageType.Unknown,
    [DisplaySection.TournamentCenter_FeaturedMatches]: AsiaBetPageType.TournamentCenterFeaturedMatches,
    [DisplaySection.TournamentCenter_Groups]: AsiaBetPageType.TournamentCenterGroups,
    [DisplaySection.TournamentCenter_Knockout]: AsiaBetPageType.TournamentCenterKnockout,
    [DisplaySection.TournamentCenter_UpcomingMatches]: AsiaBetPageType.TournamentCenterUpcomingMatches,
    [DisplaySection.TournamentCenter_Country]: AsiaBetPageType.Unknown,
    [DisplaySection.TournamentCenter_Matches]: AsiaBetPageType.TournamentCenterMatches,
};

export function useBetPage(displaySection: DisplaySection = DisplaySection.Unknown) {
    const { marketPage, daylightType, eventDate, currentPromotionZoneConfig } = storeToRefs(useAsiaSportMarketInfoStore());
    const { oddsDisplayMode } = storeToRefs(useOddsDisplaySettingsStore());

    const betPage = computed(() => {
        if (displaySection !== DisplaySection.Unknown) {
            return displaySectionMap[displaySection];
        }

        const betPageFromPromotionZone = currentPromotionZoneConfig.value?.defaultBetPage;
        if (betPageFromPromotionZone) {
            return betPageFromPromotionZone;
        }

        return getBetPageFromMarket(
            marketPage.value,
            daylightType.value,
            eventDate.value,
            oddsDisplayMode.value === OddsDisplayMode.Single,
        );
    });

    return {
        betPage,
    };
}
